import { Upload } from "@mui/icons-material";
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Typography,
} from "@mui/material";
import { FormikHelpers } from "formik";
import { ReactElement } from "react";
import * as yup from "yup";

import { plainScriptFieldValidator } from "~frontend/components/forms/FormPlainScriptField";
import { FormikScriptField } from "~frontend/components/forms/FormScriptField";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import { FormikDialog } from "~frontend/components/forms/FormikDialog";
import FormikErrorBanner from "~frontend/components/forms/FormikErrorBanner";
import {
  MediaFieldValue,
  MediaPreview,
} from "~frontend/components/forms/FormikMediaField";
import { useTenant } from "~frontend/providers/LoggedInUserProvider";
import { MEDIA_FILE_MIME_TYPES } from "~frontend/util/mediaFiles";

export interface KeywordValues {
  keyword: string;
  message: string;
  _submitError: any;
  mediaUpload?: MediaFieldValue | null;
}

export default function KeywordDialogForm({
  open,
  onClose,
  onSubmit,
  initialValues,
  dialogTitle,
  dialogCopy,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    values: KeywordValues,
    formik: FormikHelpers<KeywordValues>
  ) => void;
  initialValues: KeywordValues;
  dialogTitle: string;
  dialogCopy: string;
}): ReactElement {
  const tenant = useTenant()!;

  return (
    <FormikDialog
      open={open}
      initialValues={initialValues}
      onSubmit={onSubmit}
      validationSchema={yup.object().shape({
        keyword: yup.string().required("Required").min(1).max(100),
        message: plainScriptFieldValidator({ strictLengthLimit: true }, tenant),
      })}
    >
      {({
        isSubmitting,
        getFieldMeta,
        setFieldValue,
        setFieldTouched,
        values,
      }) => {
        const mediaFieldMeta = getFieldMeta("mediaUpload");
        const hasMedia =
          values.mediaUpload &&
          (values.mediaUpload.upload ||
            values.mediaUpload.file ||
            values.mediaUpload.url);

        return (
          <>
            <DialogTitle sx={{ pb: 0 }}>{dialogTitle}</DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
              <Typography sx={{ mb: 2 }}>{dialogCopy}</Typography>
              <FormikErrorBanner />
              <FormikTextField
                fullWidth
                label="Keyword"
                name="keyword"
                inputProps={{ style: { textTransform: "uppercase" } }}
              />
              <FormikScriptField
                label="Message Text"
                name="message"
                media={
                  hasMedia ? (
                    <>
                      <MediaPreview media={values.mediaUpload!} />
                      {mediaFieldMeta.error ? (
                        <FormHelperText error>
                          {mediaFieldMeta.error}
                        </FormHelperText>
                      ) : null}
                    </>
                  ) : null
                }
                additionalButtons={[
                  hasMedia
                    ? {
                        value: "media",
                        onClick: () => setFieldValue("mediaUpload", undefined),
                        children: "Remove Media File",
                        selected: true,
                      }
                    : {
                        value: "media",
                        component: "label",
                        children: (
                          <>
                            <Upload /> Upload a Media File
                            <input
                              type="file"
                              hidden
                              accept={MEDIA_FILE_MIME_TYPES}
                              onChange={async event => {
                                event.preventDefault();
                                if (!event.target.files?.length) {
                                  return;
                                }
                                try {
                                  await setFieldValue("mediaUpload", {
                                    file: event.target.files[0],
                                  });
                                  await setFieldTouched(
                                    "mediaUpload",
                                    true,
                                    true
                                  );
                                } catch (e) {
                                  console.error(e);
                                }
                              }}
                            />
                          </>
                        ),
                      },
                ]}
                hideContactFields
              />
              <Alert severity="warning">
                Media files can be a picture, video, GIF, or contact card. Files
                must be jpg, png, gif, mp4, or vcard format and under 500 KB.
                Attachments not meeting these guidelines will not be included in
                the message.
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button
                disabled={isSubmitting}
                onClick={onClose}
                color="tertiary"
                variant="contained"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                color={"primary"}
                variant="contained"
              >
                {isSubmitting ? "Saving..." : "Save"}
              </Button>
            </DialogActions>
          </>
        );
      }}
    </FormikDialog>
  );
}
